// Owner: record-runtime (wave 2). Record interfaces: the RecordInterfaceDefinition v2 that the record
// page, the peek, the designer canvas, previews and the phone render with one renderer (plan §7.3,
// §9.5), the props of its built-in blocks, the typed header actions, and the two legacy shapes it
// replaces (the builder's LayoutDefinition and the page templates), which stay readable so stored
// data, blueprints and the interim builder keep working while they are converted.
import { type DocumentNode } from './doc/node';
import { BlockDocument } from './doc/registry';
import { z } from 'zod';
import { ApiKey, Uuid } from './ids';
import { localizedName } from './localized-storage';
import { FieldPath, FilterNodeSchema } from './filter';
import { ComponentInputs, DeclarativeDefinition } from './custom-components';
import { Insight } from './doc/insight';
import { isPlainRecord, propertyOf } from './json';
import { type LocalizedText, localizedText, resolveText } from './localized-text';

/**
 * A record layout is a tree of widgets placed in five regions of the record page:
 *
 *   top    — full width (header, highlights)
 *   left   — narrow column (field groups)
 *   main   — wide column (tabs with composer + timeline)
 *   right  — narrow column (related lists, linked systems)
 *
 * Container widgets (`tabs`, `columns`) hold further widgets in named `slots`
 * (`tabs`: one slot per tab id, `columns`: `col1`, `col2`).
 */
export const LAYOUT_REGIONS = ['top', 'left', 'main', 'right'] as const;
export const LayoutRegion = z.enum(LAYOUT_REGIONS);
export type LayoutRegion = z.infer<typeof LayoutRegion>;

export const WIDGET_TYPES = [
  // Structure
  'document',
  'header',
  'highlights',
  'tabs',
  'columns',
  // Data
  'field_group',
  'related_list',
  'embedded_view',
  'metric',
  'chart',
  'linked_systems',
  // Activity
  'timeline',
  'composer',
  'email_thread',
  'files',
  // Extensions
  'map',
  'ai_summary',
  'plugin',
] as const;
export const WidgetType = z.enum(WIDGET_TYPES);
export type WidgetType = z.infer<typeof WidgetType>;

/** Container widgets and the widget types they may hold. */
export const CONTAINER_TYPES: readonly WidgetType[] = ['tabs', 'columns'];

export const WidgetId = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-zA-Z0-9_-]+$/);

const FieldKeys = z.array(ApiKey).max(60);
const SortSpecSchema = z.object({ field: FieldPath, dir: z.enum(['asc', 'desc']) });
export const SummarySpec = z.object({
  fn: z.enum(['count', 'sum', 'avg', 'min', 'max']),
  field: ApiKey.nullish(),
  label: z.string().max(60),
});
export type SummarySpec = z.infer<typeof SummarySpec>;

/** Per-type widget configuration. Unknown keys are rejected so typos surface in the editor. */
export const WidgetConfigs = {
  document: z.object({ document: BlockDocument }).strict(),
  header: z
    .object({
      fields: FieldKeys.default([]),
      actions: z
        .array(z.enum(['email', 'call', 'edit', 'follow', 'more']))
        .max(6)
        .default([]),
    })
    .strict(),
  highlights: z.object({ fields: FieldKeys.max(8).default([]) }).strict(),
  tabs: z
    .object({
      tabs: z
        .array(z.object({ id: WidgetId, label: z.string().min(1).max(40) }).strict())
        .min(1)
        .max(12),
    })
    .strict(),
  columns: z.object({ ratio: z.enum(['1:1', '2:1', '1:2']).default('1:1') }).strict(),
  field_group: z.object({ fields: FieldKeys.default([]), collapsed: z.boolean().default(false) }).strict(),
  related_list: z
    .object({
      /** Relation field api key on this object (e.g. `deals` on companies). */
      relation: ApiKey,
      columns: FieldKeys.max(8).default([]),
      filter: FilterNodeSchema.nullish(),
      sort: SortSpecSchema.nullish(),
      limit: z.number().int().min(1).max(50).default(5),
      summary: SummarySpec.nullish(),
      allowCreate: z.boolean().default(true),
      inlineEdit: z.boolean().default(false),
    })
    .strict(),
  embedded_view: z
    .object({
      object: ApiKey,
      filter: FilterNodeSchema.nullish(),
      limit: z.number().int().min(1).max(50).default(10),
    })
    .strict(),
  metric: z
    .object({
      relation: ApiKey.nullish(),
      fn: z.enum(['count', 'sum', 'avg', 'min', 'max']),
      field: ApiKey.nullish(),
      filter: FilterNodeSchema.nullish(),
    })
    .strict(),
  chart: z
    .object({
      relation: ApiKey,
      groupBy: ApiKey,
      fn: z.enum(['count', 'sum', 'avg']).default('count'),
      field: ApiKey.nullish(),
    })
    .strict(),
  linked_systems: z.object({}).strict(),
  timeline: z
    .object({
      kinds: z
        .array(
          z.enum(['email', 'call', 'meeting', 'note', 'stage_change', 'enrichment', 'workflow', 'change']),
        )
        .default([]),
    })
    .strict(),
  composer: z
    .object({
      modes: z
        .array(z.enum(['note', 'call', 'email', 'task']))
        .min(1)
        .default(['note', 'call', 'email']),
    })
    .strict(),
  email_thread: z.object({ limit: z.number().int().min(1).max(50).default(10) }).strict(),
  files: z.object({ field: ApiKey.nullish() }).strict(),
  map: z.object({ field: ApiKey.nullish() }).strict(),
  ai_summary: z.object({ prompt: z.string().max(500).default('') }).strict(),
  plugin: z
    .object({
      extension: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9][a-z0-9.-]*$/),
      widget: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9_.-]*$/),
    })
    .strict(),
} satisfies Record<WidgetType, z.ZodType>;

export type LayoutWidget = {
  id: string;
  type: WidgetType;
  title?: string | null | undefined;
  config: Record<string, unknown>;
  /** Visibility expression (`record.tier != "C" || viewer.role == "Sales manager"`). */
  visibleWhen?: string | null | undefined;
  /** Child widgets of container widgets, keyed by slot (tab id or `col1`/`col2`). */
  slots?: Record<string, LayoutWidget[]> | undefined;
};

export const LAYOUT_MAX_WIDGETS = 120;

export const LayoutWidgetSchema: z.ZodType<LayoutWidget> = z.lazy(() =>
  z
    .object({
      id: WidgetId,
      type: WidgetType,
      title: z.string().max(80).nullish(),
      config: z.record(z.string(), z.unknown()),
      visibleWhen: z.string().max(1000).nullish(),
      slots: z.record(WidgetId, z.array(LayoutWidgetSchema).max(40)).optional(),
    })
    .strict()
    .superRefine((w, ctx) => {
      const parsed = WidgetConfigs[w.type].safeParse(w.config);
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          ctx.addIssue({ code: 'custom', message: issue.message, path: ['config', ...issue.path] });
        }
      }
      if (w.slots && !CONTAINER_TYPES.includes(w.type)) {
        ctx.addIssue({
          code: 'custom',
          message: `${w.type} widgets cannot contain other widgets`,
          path: ['slots'],
        });
      }
    }),
);

export const LayoutDefinition = z
  .object({
    document: BlockDocument.optional(),
    regions: z
      .object({
        top: z.array(LayoutWidgetSchema).max(40),
        left: z.array(LayoutWidgetSchema).max(40),
        main: z.array(LayoutWidgetSchema).max(40),
        right: z.array(LayoutWidgetSchema).max(40),
      })
      .strict(),
  })
  .strict()
  .superRefine((def, ctx) => {
    const ids = new Set<string>();
    let count = 0;
    const walk = (list: LayoutWidget[]) => {
      for (const w of list) {
        count++;
        if (ids.has(w.id)) ctx.addIssue({ code: 'custom', message: `Duplicate widget id "${w.id}"` });
        ids.add(w.id);
        for (const children of Object.values(w.slots ?? {})) walk(children);
      }
    };
    if (def.document) {
      const visit = (node: DocumentNode) => {
        if (node.type === 'uiBlock') {
          const a = node.attrs ?? {};
          if (a.component === 'record.block') {
            // A v2 block in the interim builder's document (`interfaceDocument`); the definition it
            // converts to is checked as a whole (`RecordInterfaceDefinition`, at most 150 blocks).
            if (!InterfaceBlock.safeParse((a.props as Record<string, unknown>)?.block).success)
              ctx.addIssue({ code: 'custom', message: 'Invalid record block configuration' });
          } else if (a.component === 'record.widget') {
            const parsed = LayoutWidgetSchema.safeParse((a.props as Record<string, unknown>)?.widget);
            if (!parsed.success)
              ctx.addIssue({ code: 'custom', message: 'Invalid record block configuration' });
            else {
              if (parsed.data.id !== a.id)
                ctx.addIssue({ code: 'custom', message: 'Record block identity mismatch' });
              walk([parsed.data]);
            }
          } else if (
            a.component !== 'record.feature' ||
            ![
              'stage',
              'header_extensions',
              'sidebar_extensions',
              'template_left',
              'template_right',
              'tasks',
              'tab_extensions',
            ].includes(String((a.props as Record<string, unknown>)?.feature))
          ) {
            ctx.addIssue({ code: 'custom', message: 'Unsupported record page component' });
          }
        }
        node.content?.forEach(visit);
      };
      visit(def.document);
    } else for (const r of LAYOUT_REGIONS) walk(def.regions[r]);
    if (count > LAYOUT_MAX_WIDGETS)
      ctx.addIssue({ code: 'custom', message: `A layout holds at most ${LAYOUT_MAX_WIDGETS} widgets` });
  });
export type LayoutDefinition = z.infer<typeof LayoutDefinition>;

// ---------------------------------------------------------------------------------------------
// Page templates (legacy, read only). Before RecordInterfaceDefinition v2 every object had a page
// template (settings `recordPages.layout.<object>`: header, highlights, stage path, side widgets,
// tabs, create form) that a published builder layout was merged into. The data migration
// `record-interface-v2` turns template + published layout into one v2 definition; these schemas
// stay to read stored templates, to convert them, and for the props of the legacy components.
// `@dataroom/core/record-pages` re-exports them under their former names (`TemplateWidget`,
// `TemplateTab`, `PageTemplate`).
// ---------------------------------------------------------------------------------------------

const TemplateWidgetKey = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-zA-Z0-9_-]+$/);

export const ChipTone = z.enum(['neutral', 'accent', 'ok', 'warn', 'err', 'computed', 'external', 'line']);
export type ChipTone = z.infer<typeof ChipTone>;

/** Simple condition on the record's own values (layout rules; evaluated server side). */
export const LayoutCondition = z
  .object({
    field: z.string().min(1).max(63),
    op: z.enum(['eq', 'neq', 'in', 'not_in', 'is_empty', 'is_not_empty']),
    value: z.unknown().optional(),
  })
  .strict();
export type LayoutCondition = z.infer<typeof LayoutCondition>;

/**
 * A chip next to a value or in the header when a condition holds. Its label is content in the
 * workspace's languages (a plain string, or one text per locale) and shows in the reader's language.
 */
export const ChipRule = z
  .object({
    label: localizedText({ minLength: 1, maxLength: 40 }),
    tone: ChipTone,
    when: LayoutCondition.optional(),
  })
  .strict();
export type ChipRule = z.infer<typeof ChipRule>;

/** Arithmetic over number / currency / percent fields: `amount * (1 - discount)`. */
export type ArithExpr = number | string | { op: 'add' | 'sub' | 'mul' | 'div'; args: [ArithExpr, ArithExpr] };
export const ArithExprSchema: z.ZodType<ArithExpr> = z.lazy(() =>
  z.union([
    z.number().finite(),
    z.string().regex(/^[a-z][a-z0-9_]*$/),
    z
      .object({
        op: z.enum(['add', 'sub', 'mul', 'div']),
        args: z.tuple([ArithExprSchema, ArithExprSchema]),
      })
      .strict(),
  ]),
);

export const LayoutFieldItem = z
  .object({
    kind: z.literal('field'),
    field: z.string().min(1).max(63),
    /** Plain, or one text per language (LocalizedText): the page shows the reader's. */
    label: localizedText({ maxLength: 80 }).optional(),
    /** Extra chip next to the value (e.g. "Needs approval" when approval is pending). */
    chip: ChipRule.optional(),
    /** Show the value's provenance chip (external / computed / locked). Default true. */
    sourceChip: z.boolean().optional(),
  })
  .strict();
export type LayoutFieldItem = z.infer<typeof LayoutFieldItem>;

export const LayoutComputedItem = z
  .object({
    kind: z.literal('computed'),
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().min(1).max(80),
    expr: ArithExprSchema,
    format: z.enum(['currency', 'number', 'percent']),
    /** Currency of the result (default: currency of the first money operand). */
    currencyFrom: z.string().optional(),
    chip: ChipRule.optional(),
  })
  .strict();
export type LayoutComputedItem = z.infer<typeof LayoutComputedItem>;

export const LayoutItem = z.discriminatedUnion('kind', [LayoutFieldItem, LayoutComputedItem]);
export type LayoutItem = z.infer<typeof LayoutItem>;

export const HighlightSpec = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('field'),
      field: z.string().min(1).max(63),
      label: localizedText({ maxLength: 80 }).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('rollup'),
      key: z.string().regex(/^[a-z][a-z0-9_]*$/),
      label: localizedText({ minLength: 1, maxLength: 80 }),
      /** Relation field on this object (e.g. `deals` on companies). */
      relation: z.string().min(1).max(63),
      fn: z.enum(['sum', 'count']),
      field: z.string().max(63).optional(),
      filter: FilterNodeSchema.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('last_activity'),
      key: z.string().regex(/^[a-z][a-z0-9_]*$/),
      label: localizedText({ minLength: 1, maxLength: 80 }),
      kinds: z.array(z.string().max(40)).min(1).max(10),
    })
    .strict(),
]);
export type HighlightSpec = z.infer<typeof HighlightSpec>;

export const StageCheck = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('link'),
      key: z.string().regex(/^[a-z][a-z0-9_]*$/),
      /** Plain, or one text per language (LocalizedText): the page shows the reader's. */
      label: localizedText({ minLength: 1, maxLength: 80 }),
      relation: z.string().min(1).max(63),
      linkField: z.string().min(1).max(63),
      value: z.string().min(1).max(63),
    })
    .strict(),
  z
    .object({
      kind: z.literal('condition'),
      key: z.string().regex(/^[a-z][a-z0-9_]*$/),
      label: localizedText({ minLength: 1, maxLength: 80 }),
      condition: LayoutCondition,
    })
    .strict(),
]);
export type StageCheck = z.infer<typeof StageCheck>;

export const TemplateWidget = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('document'),
      key: TemplateWidgetKey,
      title: z.string().optional(),
      document: BlockDocument,
    })
    .strict(),
  z
    .object({
      kind: z.literal('field_groups'),
      /** Explicit groups; absent = grouped by the fields' `group`. */
      groups: z
        .array(
          z.object({ title: z.string().min(1).max(80), items: z.array(LayoutFieldItem).max(100) }).strict(),
        )
        .max(20)
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('details'),
      title: z.string().min(1).max(80),
      items: z.array(LayoutItem).max(60),
    })
    .strict(),
  z
    .object({
      kind: z.literal('stage_requirements'),
      field: z.string().min(1).max(63),
      /** Stage key to show requirements for (default: the first `won` stage of the pipeline). */
      target: z.string().max(63).optional(),
      checks: z.array(StageCheck).max(20).default([]),
    })
    .strict(),
  z
    .object({
      kind: z.literal('related_list'),
      relation: z.string().min(1).max(63),
      title: z.string().max(80).optional(),
      /** Fields of the related records shown under the title (e.g. job_title; stage, close_date, amount). */
      fields: z.array(z.string().max(63)).max(6).default([]),
      limit: z.number().int().min(1).max(20).default(4),
      /** Sum shown in the header, e.g. open amount. */
      summary: z
        .object({
          field: z.string().max(63),
          filter: FilterNodeSchema.optional(),
          label: localizedText({ maxLength: 40 }),
        })
        .strict()
        .optional(),
      style: z.enum(['people', 'cards']).default('people'),
      /** Only related records matching this filter are listed (layout editor, S5). */
      filter: FilterNodeSchema.optional(),
      /** Order of the list (default: newest change first, oldest first for people). */
      sort: z
        .object({ field: z.string().min(1).max(200), dir: z.enum(['asc', 'desc']) })
        .strict()
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('record_card'),
      relation: z.string().min(1).max(63),
      /** Subtitle fields of the linked record (e.g. industry, headquarters). */
      fields: z.array(z.string().max(63)).max(4).default([]),
      chip: z.string().max(63).optional(),
      stats: z
        .array(
          z.union([
            z
              .object({
                kind: z.literal('field'),
                field: z.string().max(63),
                label: localizedText({ maxLength: 40 }),
              })
              .strict(),
            z.preprocess(
              // Stored templates name this stat after the CRM's records; it is the count and sum of
              // the open records of a relation of the linked record.
              (v) =>
                v && typeof v === 'object' && /^open_/.test(String((v as { kind?: unknown }).kind))
                  ? { ...(v as object), kind: 'open_records' }
                  : v,
              z
                .object({
                  kind: z.literal('open_records'),
                  relation: z.string().max(63),
                  field: z.string().max(63),
                  label: localizedText({ maxLength: 40 }),
                })
                .strict(),
            ),
          ]),
        )
        .max(4)
        .default([]),
    })
    .strict(),
  z
    .object({
      kind: z.literal('approval'),
      title: z.string().min(1).max(80),
      field: z.string().min(1).max(63),
      pending: z.string().min(1).max(63),
      /** Field with the requested value (e.g. discount) shown in the text. */
      subject: z.string().max(63).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('tasks'),
      title: z.string().min(1).max(80),
    })
    .strict(),
  z.object({ kind: z.literal('linked_systems'), title: z.string().min(1).max(80) }).strict(),
  /** Record widget of an installed extension (S8), placed in the layout editor (S5 plugin widget). */
  z
    .object({
      kind: z.literal('extension'),
      extension: z.string().min(1).max(120),
      widget: z.string().min(1).max(80),
      title: z.string().max(80).optional(),
    })
    .strict(),
  // Data widgets placed in the layout editor (S5). `key` is the editor's widget id; the page
  // payload carries their values under `widgets[key]` (computed for the viewer, server side).
  /** One number: count / sum / average / min / max over linked records or of the record itself. */
  z
    .object({
      kind: z.literal('metric'),
      key: TemplateWidgetKey,
      title: z.string().max(80).optional(),
      relation: z.string().max(63).optional(),
      fn: z.enum(['count', 'sum', 'avg', 'min', 'max']),
      field: z.string().max(63).optional(),
      filter: FilterNodeSchema.optional(),
    })
    .strict(),
  /** Bars of linked records grouped by a field (count, sum or average per group). */
  z
    .object({
      kind: z.literal('chart'),
      key: TemplateWidgetKey,
      title: z.string().max(80).optional(),
      relation: z.string().min(1).max(63),
      groupBy: z.string().min(1).max(63),
      fn: z.enum(['count', 'sum', 'avg']),
      field: z.string().max(63).optional(),
    })
    .strict(),
  /** Records of another object (linked to this record when the objects are related). */
  z
    .object({
      kind: z.literal('embedded_view'),
      key: TemplateWidgetKey,
      title: z.string().max(80).optional(),
      object: z.string().min(1).max(63),
      filter: FilterNodeSchema.optional(),
      limit: z.number().int().min(1).max(50).default(10),
    })
    .strict(),
  /** The record's address (no map tiles: external tile servers are not used in v1). */
  z
    .object({
      kind: z.literal('map'),
      key: TemplateWidgetKey,
      title: z.string().max(80).optional(),
      field: z.string().max(63).optional(),
    })
    .strict(),
  /** Summary of the record written by the local, deterministic summary provider. */
  z
    .object({
      kind: z.literal('summary'),
      key: TemplateWidgetKey,
      title: z.string().max(80).optional(),
      prompt: z.string().max(500).default(''),
    })
    .strict(),
]);
export type TemplateWidget = z.infer<typeof TemplateWidget>;
/** Layout widgets whose values the page payload carries in `widgets`. */
export const DATA_WIDGET_KINDS = ['metric', 'chart', 'embedded_view', 'summary'] as const;
export type DataWidget = Extract<TemplateWidget, { kind: (typeof DATA_WIDGET_KINDS)[number] }>;

export const TemplateTab = z
  .object({
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().min(1).max(40),
    kind: z.enum([
      'activity',
      'notes',
      'emails',
      'related',
      'links',
      'files',
      'history',
      'extension',
      'widgets',
    ]),
    /** Relation field for `related` / `links` tabs. */
    relation: z.string().max(63).optional(),
    /** Columns of `related` tabs (fields of the related object). */
    columns: z.array(z.string().max(63)).max(8).optional(),
    /** Email object and the relation fields leading to it (for `emails`). */
    object: z.string().max(63).optional(),
    /** Show stage durations under a `links` tab (deal stakeholders). */
    stageHistory: z.boolean().optional(),
    /** Extension record widget shown as the tab (`extension` tabs). */
    extension: z.string().min(1).max(120).optional(),
    widget: z.string().min(1).max(80).optional(),
    /** Data widgets shown as the tab's content (`widgets` tabs: charts, metrics, views, summary). */
    widgets: z.array(TemplateWidget).max(10).optional(),
  })
  .strict();
export type TemplateTab = z.infer<typeof TemplateTab>;

export const HeaderAction = z.union([
  z.enum(['email', 'log_call', 'mark_won', 'mark_lost', 'more']),
  z.object({ kind: z.literal('create'), object: z.string().max(63), label: z.string().max(40) }).strict(),
]);
export type HeaderAction = z.infer<typeof HeaderAction>;

export const PageTemplate = z
  .object({
    document: BlockDocument.optional(),
    documentBlocks: z
      .record(
        z.string(),
        z.object({ widget: TemplateWidget.optional(), tab: TemplateTab.optional() }).strict(),
      )
      .optional(),
    objectKey: z.string(),
    name: z.string().max(80).default('Default'),
    header: z
      .object({
        /** Items after the title: fields rendered as link (domain), text (location) or relation link. */
        subtitle: z
          .array(
            z
              .object({
                field: z.string().max(63),
                format: z.enum(['text', 'link', 'money', 'closes']).default('text'),
              })
              .strict(),
          )
          .max(6)
          .default([]),
        /** Select fields shown as chips. */
        chips: z
          .array(z.object({ field: z.string().max(63), tone: ChipTone.optional() }).strict())
          .max(6)
          .default([]),
        /** Chip rules (e.g. "Approval pending"). */
        badges: z.array(ChipRule).max(4).default([]),
        owner: z.string().max(63).optional(),
        actions: z.array(HeaderAction).max(8).default([]),
      })
      .strict(),
    highlights: z.array(HighlightSpec).max(8).default([]),
    stagePath: z
      .object({ field: z.string().max(63) })
      .strict()
      .optional(),
    left: z.array(TemplateWidget).max(40).default([]),
    /** Widgets of the center column above the tabs (layout editor widgets placed in "main"). */
    center: z.array(TemplateWidget).max(120).default([]),
    tabs: z.array(TemplateTab).max(120).default([]),
    right: z.array(TemplateWidget).max(40).default([]),
    /** Create form: fields shown first (rest behind "Show N more fields"). */
    create: z
      .object({ fields: z.array(z.string().max(63)).max(20), description: z.string().max(120).optional() })
      .strict()
      .optional(),
  })
  .strict();
export type PageTemplate = z.infer<typeof PageTemplate>;
export type PageTemplateInput = z.input<typeof PageTemplate>;

// ---------------------------------------------------------------------------------------------
// RecordInterfaceDefinition v2 (plan §7.3). One definition per layout variant describes the whole
// record page: regions place blocks by id, `blocks` holds each block's type, typed props,
// visibility rule and phone behaviour. One renderer (web `RecordInterface`) draws it for the page,
// the peek, the designer canvas, previews and the phone. Block types are registered components
// (record-runtime's built-ins, legacy components, and contributions of other areas); the props of
// the built-in types are typed here and checked on every write (`interfaceIssues`).
// ---------------------------------------------------------------------------------------------

/** Block, tab, row and cell ids: stable, url-safe. */
export const BlockId = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9_-]+$/);
export type BlockId = z.infer<typeof BlockId>;

/** Block types: `field-section`, `related-list`, `my-area.widget` (lower case, dots and dashes). */
export const BlockType = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/);

export const INTERFACE_REGIONS = [
  'header',
  'keyFacts',
  'stageBar',
  'main',
  'rail',
  'tabs',
  'createForm',
] as const;
export type InterfaceRegion = (typeof INTERFACE_REGIONS)[number];

/** How a block behaves on phones: shown, left out, or folded behind its title. */
export const BlockMobile = z.enum(['show', 'hide', 'collapse']);
export type BlockMobile = z.infer<typeof BlockMobile>;

/**
 * One cell of the main grid: a stack of blocks spanning `span` of 12 columns. `tabs: true` marks the
 * cell that hosts the tabs region below its blocks (else the tabs follow the grid at full width).
 */
export const GridCell = z
  .object({
    id: BlockId,
    span: z.number().int().min(1).max(12),
    blocks: z.array(BlockId).max(60),
    tabs: z.boolean().optional(),
  })
  .strict();
export type GridCell = z.infer<typeof GridCell>;

export const GridRow = z.object({ id: BlockId, cells: z.array(GridCell).min(1).max(4) }).strict();
export type GridRow = z.infer<typeof GridRow>;

/** The main region: rows of cells on a 12-column grid. */
export const GridRegion = z.object({ rows: z.array(GridRow).max(20) }).strict();
export type GridRegion = z.infer<typeof GridRegion>;

export const InterfaceTab = z
  .object({
    id: BlockId,
    label: localizedText({ maxLength: 40, minLength: 1 }),
    blocks: z.array(BlockId).max(20),
  })
  .strict();
export type InterfaceTab = z.infer<typeof InterfaceTab>;

export const InterfaceBlock = z
  .object({
    type: BlockType,
    /** Typed by the block type (`blockPropsSchema(type)` for the built-in ones). */
    props: z.record(z.string(), z.unknown()),
    /** Title shown on the block's card (blocks fall back to their own, e.g. the relation's label). */
    title: localizedText({ maxLength: 80, minLength: 1 }).optional(),
    /** Visibility rule: a boolean expression of the visibility context (`record.*`, `viewer.*`). */
    visibleWhen: z.string().max(1000).optional(),
    mobile: BlockMobile.default('show'),
    /** Designer lock: protects placement and props from accidental edits. */
    locked: z.boolean().optional(),
  })
  .strict();
export type InterfaceBlock = z.infer<typeof InterfaceBlock>;
export type InterfaceBlockInput = z.input<typeof InterfaceBlock>;

export const INTERFACE_MAX_BLOCKS = 150;

const InterfaceShape = z
  .object({
    version: z.literal(2),
    regions: z
      .object({
        header: z.array(BlockId).max(4),
        keyFacts: z.array(BlockId).max(4),
        stageBar: BlockId.nullable(),
        main: GridRegion,
        rail: z.array(BlockId).max(40),
        tabs: z.array(InterfaceTab).max(12),
        createForm: z.array(BlockId).max(10),
      })
      .strict(),
    blocks: z.record(BlockId, InterfaceBlock),
  })
  .strict();

/** Where each block is placed (`main.<row>.<cell>`, `tabs.<tab>`, …), in reading order. */
export function blockPlacements(def: {
  regions: z.infer<typeof InterfaceShape>['regions'];
}): Array<{ id: string; region: InterfaceRegion; path: string; tab?: string }> {
  const r = def.regions;
  const out: Array<{ id: string; region: InterfaceRegion; path: string; tab?: string }> = [];
  r.header.forEach((id, i) => out.push({ id, region: 'header', path: `regions.header.${i}` }));
  r.keyFacts.forEach((id, i) => out.push({ id, region: 'keyFacts', path: `regions.keyFacts.${i}` }));
  if (r.stageBar) out.push({ id: r.stageBar, region: 'stageBar', path: 'regions.stageBar' });
  r.main.rows.forEach((row, ri) =>
    row.cells.forEach((cell, ci) =>
      cell.blocks.forEach((id, bi) =>
        out.push({ id, region: 'main', path: `regions.main.rows.${ri}.cells.${ci}.blocks.${bi}` }),
      ),
    ),
  );
  r.rail.forEach((id, i) => out.push({ id, region: 'rail', path: `regions.rail.${i}` }));
  r.tabs.forEach((tab, ti) =>
    tab.blocks.forEach((id, bi) =>
      out.push({ id, region: 'tabs', path: `regions.tabs.${ti}.blocks.${bi}`, tab: tab.id }),
    ),
  );
  r.createForm.forEach((id, i) => out.push({ id, region: 'createForm', path: `regions.createForm.${i}` }));
  return out;
}

/**
 * The v2 definition. Structure is checked here: every placed block exists, every block is placed
 * exactly once, ids are unique, the stage bar is a `stage-bar` block, and the props of built-in
 * block types match their schema. Field references are checked against the model by the layout
 * service (`validateInterface` in `@dataroom/studio`).
 */
/** Placement contract shared by server authoring and native interface editors. Unknown contributed
 * blocks retain their extension-owned regions and validators. */
export const BUILTIN_INTERFACE_REGIONS: Record<string, readonly InterfaceRegion[]> = {
  header: ['header'],
  'key-facts': ['keyFacts', 'main', 'rail'],
  'stage-bar': ['stageBar'],
  'field-section': ['main', 'rail', 'tabs', 'createForm'],
  requirements: ['main', 'rail'],
  approval: ['main', 'rail'],
  insight: ['main', 'rail', 'tabs'],
  'related-grid': ['main', 'rail', 'tabs'],
  'linked-cards': ['main', 'rail', 'tabs'],
  'record-card': ['main', 'rail', 'tabs'],
};

export const RecordInterfaceDefinition = InterfaceShape.superRefine((def, ctx) => {
  const placed = new Map<string, string>();
  for (const p of blockPlacements(def)) {
    if (!def.blocks[p.id])
      ctx.addIssue({ code: 'custom', path: p.path.split('.'), message: `Unknown block "${p.id}"` });
    const block = def.blocks[p.id];
    const allowed = block ? BUILTIN_INTERFACE_REGIONS[block.type] : undefined;
    if (allowed && !allowed.includes(p.region))
      ctx.addIssue({
        code: 'custom',
        path: p.path.split('.'),
        message: `Component "${block!.type}" cannot be placed in ${p.region}`,
      });
    const before = placed.get(p.id);
    if (before)
      ctx.addIssue({
        code: 'custom',
        path: p.path.split('.'),
        message: `Block "${p.id}" is placed twice (also at ${before})`,
      });
    placed.set(p.id, p.path);
  }
  const ids = Object.keys(def.blocks);
  for (const id of ids)
    if (!placed.has(id))
      ctx.addIssue({
        code: 'custom',
        path: ['blocks', id],
        message: `Block "${id}" is not placed in a region`,
      });
  if (ids.length > INTERFACE_MAX_BLOCKS)
    ctx.addIssue({
      code: 'custom',
      path: ['blocks'],
      message: `An interface holds at most ${INTERFACE_MAX_BLOCKS} blocks`,
    });
  const containers = new Set<string>();
  const unique = (id: string, path: (string | number)[]) => {
    if (containers.has(id) || def.blocks[id])
      ctx.addIssue({ code: 'custom', path, message: `The id "${id}" is used twice` });
    containers.add(id);
  };
  def.regions.main.rows.forEach((row, ri) => {
    unique(row.id, ['regions', 'main', 'rows', ri, 'id']);
    row.cells.forEach((cell, ci) => unique(cell.id, ['regions', 'main', 'rows', ri, 'cells', ci, 'id']));
    const span = row.cells.reduce((sum, c) => sum + c.span, 0);
    if (span > 12)
      ctx.addIssue({
        code: 'custom',
        path: ['regions', 'main', 'rows', ri, 'cells'],
        message: 'The cells of a row span at most 12 columns',
      });
  });
  def.regions.tabs.forEach((tab, ti) => unique(tab.id, ['regions', 'tabs', ti, 'id']));
  const stage = def.regions.stageBar ? def.blocks[def.regions.stageBar] : undefined;
  if (stage && stage.type !== 'stage-bar')
    ctx.addIssue({
      code: 'custom',
      path: ['regions', 'stageBar'],
      message: 'The stage bar region holds a stage-bar block',
    });
  for (const [id, block] of Object.entries(def.blocks)) {
    const schema = BUILTIN_BLOCK_PROPS[block.type as BuiltinBlockType];
    if (!schema) continue;
    const parsed = schema.safeParse(block.props);
    if (!parsed.success)
      for (const issue of parsed.error.issues)
        ctx.addIssue({
          code: 'custom',
          path: ['blocks', id, 'props', ...issue.path.map((p) => (typeof p === 'symbol' ? String(p) : p))],
          message: issue.message,
        });
  }
});
export type RecordInterfaceDefinition = z.infer<typeof InterfaceShape>;
export type RecordInterfaceDefinitionInput = z.input<typeof InterfaceShape>;

/** True for a v2 definition (as opposed to a legacy builder `LayoutDefinition`). */
export function isInterfaceDefinition(def: unknown): def is RecordInterfaceDefinition {
  return propertyOf(def, 'version') === 2;
}

/** A definition as a client may send it: v2, or the legacy builder shape (converted on write). */
export const AnyLayoutDefinition = z.union([RecordInterfaceDefinition, LayoutDefinition]);
export type AnyLayoutDefinition = RecordInterfaceDefinition | LayoutDefinition;

// -- typed header actions (plan §9.5, record-views §3.2) -------------------------------------------

const ActionCommon = {
  /** Stable id within the header (the designer selects actions by it). */
  id: BlockId,
  /** Button text; each kind has a localized default ("Email", "Log activity", "New Deal"). */
  label: localizedText({ maxLength: 40, minLength: 1 }).optional(),
  /** Visibility rule (visibility context, boolean). */
  visibleWhen: z.string().max(1000).optional(),
  /** The one primary action of the header (principle 11); at most one per header. */
  primary: z.boolean().optional(),
};

export const RecordHeaderAction = z.discriminatedUnion('kind', [
  /** Create a record of a related object, linked back to this one (`relation`: to-many relation field). */
  z.object({ kind: z.literal('createRelated'), relation: ApiKey, ...ActionCommon }).strict(),
  /**
   * Write an e-mail through the in-app composer (`openLayer('composer')`). Recipients: the record's
   * own e-mail field, else the e-mail fields of linked people (`recipients`: a field path to use).
   */
  z.object({ kind: z.literal('composeEmail'), recipients: FieldPath.optional(), ...ActionCommon }).strict(),
  /** Log a call, meeting or note on the record. */
  z.object({ kind: z.literal('logActivity'), ...ActionCommon }).strict(),
  /**
   * Move to the first stage of a closed stage group (`won`: closed successfully; `lost`: closed
   * unsuccessfully, asks for the reason).
   */
  z.object({ kind: z.literal('moveToStageGroup'), group: z.enum(['won', 'lost']), ...ActionCommon }).strict(),
  /** Slot for "Run automation" (rendered by the automations area's record action). */
  z
    .object({ kind: z.literal('runAutomation'), automation: z.string().max(120).optional(), ...ActionCommon })
    .strict(),
  /** Open a link built from the record: a template (`https://maps.example/?q={{ city }}`). */
  z
    .object({
      kind: z.literal('openUrl'),
      url: z.string().min(1).max(2000),
      ...ActionCommon,
      label: localizedText({ maxLength: 40, minLength: 1 }),
    })
    .strict(),
]);
export type RecordHeaderAction = z.infer<typeof RecordHeaderAction>;
export type RecordHeaderActionKind = RecordHeaderAction['kind'];

// -- props of the built-in blocks ------------------------------------------------------------------

const FieldKey = z.string().min(1).max(63);

/** Header: title, subtitle values, chips, badges, owner and the typed actions. */
export const HeaderBlockProps = z
  .object({
    subtitle: z
      .array(
        z
          .object({ field: FieldKey, format: z.enum(['text', 'link', 'money', 'closes']).default('text') })
          .strict(),
      )
      .max(6)
      .default([]),
    chips: z
      .array(z.object({ field: FieldKey, tone: ChipTone.optional() }).strict())
      .max(6)
      .default([]),
    badges: z.array(ChipRule).max(4).default([]),
    owner: FieldKey.optional(),
    actions: z.array(RecordHeaderAction).max(8).default([]),
  })
  .strict();
export type HeaderBlockProps = z.infer<typeof HeaderBlockProps>;

/** Key facts: 1–8 values (fields, rollups over a relation, the last activity). */
export const KeyFactsBlockProps = z.object({ items: z.array(HighlightSpec).max(8).default([]) }).strict();
export type KeyFactsBlockProps = z.infer<typeof KeyFactsBlockProps>;

/** Stage bar of a status field: click to move (with Undo), "Close as ▾" for won and lost stages. */
export const StageBarBlockProps = z
  .object({
    field: FieldKey,
    /** Moving to an earlier stage asks first. */
    confirmBackward: z.boolean().default(true),
    /** Offer "Close as ▾" with every won and lost stage. */
    closeAs: z.boolean().default(true),
  })
  .strict();
export type StageBarBlockProps = z.infer<typeof StageBarBlockProps>;

/** What a field of the create form starts with (see `FieldSectionBlockProps.defaults`). */
export const FieldDefault = z.union([
  z.object({ field: FieldKey, value: z.json(), hidden: z.boolean().optional() }).strict(),
  z.object({ field: FieldKey, mine: FieldKey, hidden: z.boolean().optional() }).strict(),
]);
export type FieldDefault = z.infer<typeof FieldDefault>;

/**
 * Field section: `details` is one titled list of values (plus calculated rows); `groups` shows
 * titled groups in one card (`groups: null` = by the fields' own group), with "Show N empty fields".
 */
export const FieldSectionBlockProps = z
  .object({
    /** Create-form region: false limits creation to the configured fields. Other regions ignore it. */
    allowAdditionalFields: z.boolean().optional(),
    style: z.enum(['details', 'groups']).default('groups'),
    /**
     * Create-form region: what a new record starts with. A `value` (a plain value, or one text per language for a
     * text field) or `mine`: for a relation field, the record of the related object whose user field `mine` is the
     * member who creates ("my employee record"). A `hidden` default is not asked: the value is simply saved.
     */
    defaults: z.array(FieldDefault).max(20).optional(),
    /** Create-form region: fields that must be filled in this form (marked with a star) besides those the object requires. */
    requiredFields: z.array(FieldKey).max(30).optional(),
    items: z.array(LayoutItem).max(60).default([]),
    groups: z
      .array(
        z
          .object({
            title: localizedText({ maxLength: 80, minLength: 1 }),
            items: z.array(LayoutFieldItem).max(100),
          })
          .strict(),
      )
      .max(20)
      .nullable()
      .default(null),
  })
  .strict();
export type FieldSectionBlockProps = z.infer<typeof FieldSectionBlockProps>;

/** What the record still needs before it may enter a stage (required fields and checks). */
export const RequirementsBlockProps = z
  .object({
    field: FieldKey,
    target: z.string().max(63).optional(),
    checks: z.array(StageCheck).max(20).default([]),
  })
  .strict();
export type RequirementsBlockProps = z.infer<typeof RequirementsBlockProps>;

/** State of an approval kept in a choice field (`pending` = the value while it waits). */
export const ApprovalBlockProps = z
  .object({ field: FieldKey, pending: z.string().min(1).max(63), subject: FieldKey.optional() })
  .strict();
export type ApprovalBlockProps = z.infer<typeof ApprovalBlockProps>;

/** A chart or metric (Insight), evaluated with the record as context. */
export const InsightBlockProps = z.object({ insight: Insight }).strict();
export type InsightBlockProps = z.infer<typeof InsightBlockProps>;

// Legacy components (the record page before v2, one file each under `features/records/legacy/`;
// record-related, record-activity and home-inbox replace them by registering the same type).
export const RelatedListBlockProps = z
  .object({
    relation: FieldKey,
    fields: z.array(FieldKey).max(6).default([]),
    limit: z.number().int().min(1).max(20).default(4),
    summary: z
      .object({
        field: FieldKey,
        filter: FilterNodeSchema.optional(),
        label: localizedText({ maxLength: 40 }),
      })
      .strict()
      .optional(),
    style: z.enum(['people', 'cards']).default('people'),
    filter: FilterNodeSchema.optional(),
    sort: z
      .object({ field: z.string().min(1).max(200), dir: z.enum(['asc', 'desc']) })
      .strict()
      .optional(),
  })
  .strict();
export const RecordCardBlockProps = z
  .object({
    relation: FieldKey,
    fields: z.array(FieldKey).max(4).default([]),
    chip: FieldKey.optional(),
    stats: z
      .array(
        z.union([
          z
            .object({
              kind: z.literal('field'),
              field: FieldKey,
              label: localizedText({ maxLength: 40 }),
            })
            .strict(),
          z
            .object({
              kind: z.literal('open_records'),
              relation: FieldKey,
              field: FieldKey,
              label: localizedText({ maxLength: 40 }),
            })
            .strict(),
        ]),
      )
      .max(4)
      .default([]),
  })
  .strict();
export const MetricBlockProps = z
  .object({
    relation: FieldKey.optional(),
    fn: z.enum(['count', 'sum', 'avg', 'min', 'max']),
    field: FieldKey.optional(),
    filter: FilterNodeSchema.optional(),
  })
  .strict();
export const EmbeddedViewBlockProps = z
  .object({
    object: FieldKey,
    filter: FilterNodeSchema.optional(),
    limit: z.number().int().min(1).max(50).default(10),
  })
  .strict();
export const AddressBlockProps = z.object({ field: FieldKey.optional() }).strict();
/**
 * The rule-based record summary: which sections it writes (null = from the older free-text
 * `prompt`, which picks sections by generic words; else all four).
 */
export const SummaryBlockProps = z
  .object({
    sections: z
      .array(z.enum(['profile', 'pipeline', 'activity', 'risks']))
      .max(4)
      .nullable()
      .default(null),
    prompt: z.string().max(500).default(''),
  })
  .strict();
export const DocumentBlockProps = z.object({ document: BlockDocument }).strict();
export const ExtensionBlockProps = z
  .object({ extension: z.string().min(1).max(120), widget: z.string().min(1).max(80) })
  .strict();
export const ActivityBlockProps = z
  .object({
    /** Timeline kinds shown (null = all); notes only = `['note']`. */
    kinds: z.array(z.string().min(1).max(40)).max(10).nullable().default(null),
    composer: z.boolean().default(true),
  })
  .strict();
export const EmailsBlockProps = z.object({ object: FieldKey.optional() }).strict();
export const RelatedTableBlockProps = z
  .object({ relation: FieldKey, columns: z.array(FieldKey).max(8).default([]) })
  .strict();
export const LinkTableBlockProps = z
  .object({ relation: FieldKey, stageHistory: z.boolean().default(false) })
  .strict();
/** A custom component (F20): which one, and what each of its props gets from the page (a literal or an expression). */
export const ComponentBlockProps = z
  .object({ componentId: z.string().max(64).default(''), inputs: ComponentInputs })
  .strict();
export type ComponentBlockProps = z.infer<typeof ComponentBlockProps>;
/**
 * An element of the component library placed on the record page itself: its definition lives in the layout and is
 * published with it, the page's record is its `record` prop. Declarative only (no frame code); it reads its data as the
 * viewing member like an element of a slide.
 */
export const InlineComponentBlockProps = z
  .object({
    definition: DeclarativeDefinition,
    /** `card`: on a card like the other blocks of the page; `none`: the elements alone (they bring their own cards). */
    frame: z.enum(['card', 'none']).default('card'),
  })
  .strict();
export type InlineComponentBlockProps = z.infer<typeof InlineComponentBlockProps>;
const NoProps = z.object({}).strict();

/** Props schemas of the block types this module knows (contributed types validate on the client). */
export const BUILTIN_BLOCK_PROPS = {
  header: HeaderBlockProps,
  'key-facts': KeyFactsBlockProps,
  'stage-bar': StageBarBlockProps,
  'field-section': FieldSectionBlockProps,
  requirements: RequirementsBlockProps,
  approval: ApprovalBlockProps,
  insight: InsightBlockProps,
  'related-list': RelatedListBlockProps,
  'linked-cards': RelatedListBlockProps,
  'related-grid': RelatedTableBlockProps.extend({
    style: z.enum(['grid', 'cards', 'people']).default('grid'),
  }),
  'record-card': RecordCardBlockProps,
  metric: MetricBlockProps,
  'embedded-view': EmbeddedViewBlockProps,
  address: AddressBlockProps,
  summary: SummaryBlockProps,
  document: DocumentBlockProps,
  documents: NoProps,
  extension: ExtensionBlockProps,
  tasks: NoProps,
  'linked-systems': NoProps,
  activity: ActivityBlockProps,
  emails: EmailsBlockProps,
  'related-table': RelatedTableBlockProps,
  'link-table': LinkTableBlockProps,
  component: ComponentBlockProps,
  'inline-component': InlineComponentBlockProps,
  /** The same in a task layout: `record` is the task. */
  'task-inline-component': InlineComponentBlockProps,
  files: NoProps,
  history: NoProps,
} as const satisfies Record<string, z.ZodType>;
export type BuiltinBlockType = keyof typeof BUILTIN_BLOCK_PROPS;
export type BuiltinBlockProps<T extends BuiltinBlockType> = z.infer<(typeof BUILTIN_BLOCK_PROPS)[T]>;

/** The props schema of a built-in block type (undefined for contributed types). */
export function blockPropsSchema(type: string): z.ZodType | undefined {
  return (BUILTIN_BLOCK_PROPS as Record<string, z.ZodType>)[type];
}

/** A block's props parsed with its type's schema (defaults filled); null when they do not match. */
export function blockProps<T extends BuiltinBlockType>(
  block: Pick<InterfaceBlock, 'type' | 'props'>,
  type: T,
): BuiltinBlockProps<T> | null {
  if (block.type !== type) return null;
  const r = BUILTIN_BLOCK_PROPS[type].safeParse(block.props);
  return r.success ? (r.data as BuiltinBlockProps<T>) : null;
}

// -- helpers ---------------------------------------------------------------------------------------

/** An empty v2 definition (no blocks). */
export function emptyInterface(): RecordInterfaceDefinition {
  return {
    version: 2,
    regions: {
      header: [],
      keyFacts: [],
      stageBar: null,
      main: { rows: [] },
      rail: [],
      tabs: [],
      createForm: [],
    },
    blocks: {},
  };
}

/** Every block with its id and placement, in reading order. */
export function interfaceBlocks(
  def: RecordInterfaceDefinition,
): Array<{ id: string; block: InterfaceBlock; region: InterfaceRegion; path: string; tab?: string }> {
  return blockPlacements(def).flatMap((p) => {
    const block = def.blocks[p.id];
    return block ? [{ ...p, block }] : [];
  });
}

/** The definition without the given blocks (removed from their regions; empty cells and tabs stay). */
export function withoutBlocks(
  def: RecordInterfaceDefinition,
  hidden: ReadonlySet<string>,
): RecordInterfaceDefinition {
  if (hidden.size === 0) return def;
  const keep = (ids: string[]) => ids.filter((id) => !hidden.has(id));
  return {
    version: 2,
    regions: {
      header: keep(def.regions.header),
      keyFacts: keep(def.regions.keyFacts),
      stageBar: def.regions.stageBar && !hidden.has(def.regions.stageBar) ? def.regions.stageBar : null,
      main: {
        rows: def.regions.main.rows.map((row) => ({
          ...row,
          cells: row.cells.map((cell) => ({ ...cell, blocks: keep(cell.blocks) })),
        })),
      },
      rail: keep(def.regions.rail),
      tabs: def.regions.tabs.map((tab) => ({ ...tab, blocks: keep(tab.blocks) })),
      createForm: keep(def.regions.createForm),
    },
    blocks: Object.fromEntries(Object.entries(def.blocks).filter(([id]) => !hidden.has(id))),
  };
}

/** Visibility rules of a definition with their paths (`blocks.<id>.visibleWhen`, header actions). */
export function interfaceConditions(def: RecordInterfaceDefinition): Array<{ path: string; source: string }> {
  const out: Array<{ path: string; source: string }> = [];
  for (const [id, block] of Object.entries(def.blocks)) {
    if (block.visibleWhen?.trim()) out.push({ path: `blocks.${id}.visibleWhen`, source: block.visibleWhen });
    if (block.type === 'header') {
      const actions = block.props.actions;
      if (Array.isArray(actions))
        actions.forEach((a, i) => {
          const when = propertyOf(a, 'visibleWhen');
          if (typeof when === 'string' && when.trim())
            out.push({ path: `blocks.${id}.props.actions.${i}.visibleWhen`, source: when });
        });
    }
  }
  return out;
}

/** Set one visibility rule by its path (as listed by `interfaceConditions`). */
export function withCondition(
  def: RecordInterfaceDefinition,
  path: string,
  source: string,
): RecordInterfaceDefinition {
  const next = structuredClone(def);
  const parts = path.split('.');
  if (parts[0] !== 'blocks' || !parts[1]) return def;
  const block = next.blocks[parts[1]];
  if (!block) return def;
  if (parts[2] === 'visibleWhen') block.visibleWhen = source;
  else if (parts[2] === 'props' && parts[3] === 'actions' && parts[5] === 'visibleWhen') {
    const actions = block.props.actions;
    const a = Array.isArray(actions) ? actions[Number(parts[4])] : undefined;
    if (isPlainRecord(a)) a.visibleWhen = source;
  }
  return next;
}

export const LayoutDTO = z.object({
  id: Uuid,
  objectKey: z.string(),
  key: z.string(),
  name: z.string(),
  /** Role keys this variant applies to; empty = all roles. */
  roles: z.array(z.string()),
  teams: z.array(Uuid).optional(),
  publishedRoles: z.array(z.string()).optional(),
  publishedTeams: z.array(Uuid).optional(),
  isDefault: z.boolean(),
  /** The draft and the published version, always as v2 (legacy stored shapes are converted on read). */
  draft: RecordInterfaceDefinition,
  published: RecordInterfaceDefinition.nullable(),
  /** Number of edits since the last publish (0 = draft equals published). */
  draftChanges: z.number().int(),
  version: z.number().int(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  updatedAt: z.string(),
});
export type LayoutDTO = z.infer<typeof LayoutDTO>;

export const LayoutKey = z
  .string()
  .min(1)
  .max(63)
  .regex(/^[a-z][a-z0-9_-]*$/);

export const CreateLayoutInput = z
  .object({
    /** Plain, or one name per language (LocalizedText, plan §8.10). */
    name: localizedName(80),
    key: LayoutKey.optional(),
    roles: z.array(ApiKey).max(20).default([]),
    teams: z.array(Uuid).max(50).default([]),
    /** Copy the draft of this layout (variant from an existing layout). */
    copyFrom: Uuid.optional(),
    /** v2, or a legacy builder definition (converted to v2 before it is stored). */
    definition: AnyLayoutDefinition.optional(),
  })
  .strict();
export type CreateLayoutInput = z.input<typeof CreateLayoutInput>;

export const UpdateLayoutInput = z
  .object({
    /** A plain name sets the writer's language when the name is localized. */
    name: localizedName(80),
    roles: z.array(ApiKey).max(20),
    teams: z.array(Uuid).max(50),
    /** Replace the draft (autosave): v2, or a legacy builder definition (converted to v2). */
    draft: AnyLayoutDefinition,
    /** Optimistic concurrency on the layout version. */
    expectedVersion: z.number().int().positive(),
  })
  .partial()
  .strict();
export type UpdateLayoutInput = z.input<typeof UpdateLayoutInput>;

/** What the record page renders for a viewer (published layout for the viewer's role). */
export const EffectiveLayoutDTO = z.object({
  layoutId: Uuid.nullable(),
  name: z.string(),
  definition: RecordInterfaceDefinition,
  /** `generated` when no layout is published yet and a default was derived from the model. */
  origin: z.enum(['published', 'generated']),
});
export type EffectiveLayoutDTO = z.infer<typeof EffectiveLayoutDTO>;

export const LayoutRoleDTO = z.object({ key: z.string(), label: z.string() });
export type LayoutRoleDTO = z.infer<typeof LayoutRoleDTO>;

/** Iterate every widget of a layout (depth first), with its region and parent. */
export function walkLayout(
  def: LayoutDefinition,
  fn: (w: LayoutWidget, region: LayoutRegion, parent: LayoutWidget | null) => void,
): void {
  const visit = (list: LayoutWidget[], region: LayoutRegion, parent: LayoutWidget | null) => {
    for (const w of list) {
      fn(w, region, parent);
      for (const children of Object.values(w.slots ?? {})) visit(children, region, w);
    }
  };
  if (def.document) {
    const nodes = (n: DocumentNode) => {
      if (n.type === 'uiBlock' && n.attrs?.component === 'record.widget') {
        const w = (n.attrs.props as { widget: LayoutWidget }).widget;
        visit([w], w.type === 'header' || w.type === 'highlights' ? 'top' : 'main', null);
      }
      n.content?.forEach(nodes);
    };
    nodes(def.document);
  } else for (const r of LAYOUT_REGIONS) visit(def.regions[r], r, null);
}

export function emptyLayout(): LayoutDefinition {
  return { regions: { top: [], left: [], main: [], right: [] } };
}

// -- the interim builder's documents of v2 definitions ---------------------------------------------

/** Where a `record.block` node of a builder document belongs in the v2 regions. */
export type BlockPlacement = {
  region: InterfaceRegion;
  row?: { id: string; index: number };
  cell?: { id: string; index: number; span: number; tabs?: boolean };
  tab?: { id: string; label: LocalizedText };
  /** For tab blocks: the main-grid cell that hosts the tabs (its ids survive the editor). */
  host?: { row: { id: string; index: number }; cell: { id: string; index: number; span: number } };
};

/** The node component of v2 blocks in builder documents. */
export const RECORD_BLOCK_COMPONENT = 'record.block';

function blockNode(id: string, block: InterfaceBlock, place: BlockPlacement): DocumentNode {
  return {
    type: 'uiBlock',
    attrs: { id, component: RECORD_BLOCK_COMPONENT, props: { block: structuredClone(block), place } },
  };
}

/** A v2 definition as a builder document (regions in reading order; the main grid as columns). */
export function interfaceDocument(def: RecordInterfaceDefinition): DocumentNode {
  const node = (id: string, place: BlockPlacement) => {
    const block = def.blocks[id];
    return block ? [blockNode(id, block, place)] : [];
  };
  const top: DocumentNode[] = [
    ...def.regions.header.flatMap((id) => node(id, { region: 'header' })),
    ...def.regions.keyFacts.flatMap((id) => node(id, { region: 'keyFacts' })),
    ...(def.regions.stageBar ? node(def.regions.stageBar, { region: 'stageBar' }) : []),
  ];
  const tabNodes = (host?: BlockPlacement['host']): DocumentNode[] =>
    def.regions.tabs.length
      ? [
          {
            type: 'tabs',
            attrs: { visibleWhen: null },
            content: def.regions.tabs.map((tab) => ({
              type: 'tab',
              attrs: { title: resolveText(tab.label, 'en') },
              content: [
                ...tab.blocks.flatMap((id) =>
                  node(id, {
                    region: 'tabs',
                    tab: { id: tab.id, label: tab.label },
                    ...(host ? { host } : {}),
                  }),
                ),
                { type: 'paragraph' },
              ],
            })),
          },
        ]
      : [];
  const tabsCell = def.regions.main.rows.flatMap((r) => r.cells).some((c) => c.tabs);
  const railColumn = (): DocumentNode => ({
    type: 'column',
    content: [...def.regions.rail.flatMap((id) => node(id, { region: 'rail' })), { type: 'paragraph' }],
  });
  const rows: DocumentNode[] = def.regions.main.rows.map((row, ri) => {
    const cells: DocumentNode[] = row.cells.map((cell, ci) => ({
      type: 'column',
      content: [
        ...cell.blocks.flatMap((id) =>
          node(id, {
            region: 'main',
            row: { id: row.id, index: ri },
            cell: { id: cell.id, index: ci, span: cell.span, ...(cell.tabs ? { tabs: true } : {}) },
          }),
        ),
        ...(cell.tabs
          ? tabNodes({ row: { id: row.id, index: ri }, cell: { id: cell.id, index: ci, span: cell.span } })
          : []),
        { type: 'paragraph' },
      ],
    }));
    // The rail is the last column of the first row (as on the page at full width).
    if (ri === 0) cells.push(railColumn());
    return {
      type: 'columns',
      attrs: { ratio: cells.length === 3 ? 'wide-center' : 'equal' },
      content: cells,
    };
  });
  if (rows.length === 0)
    rows.push({
      type: 'columns',
      attrs: { ratio: 'equal' },
      content: [{ type: 'column', content: [...tabNodes(), { type: 'paragraph' }] }, railColumn()],
    });
  return {
    type: 'doc',
    content: [
      ...top,
      ...rows,
      ...(tabsCell || def.regions.main.rows.length === 0 ? [] : tabNodes()),
      ...def.regions.createForm.flatMap((id) => node(id, { region: 'createForm' })),
      { type: 'paragraph' },
    ],
  };
}

/** True when a builder document holds v2 blocks (`interfaceFromDocument` applies). */
export function isInterfaceDocument(doc: DocumentNode | undefined): boolean {
  let found = false;
  const visit = (n: DocumentNode) => {
    if (n.type === 'uiBlock' && n.attrs?.component === RECORD_BLOCK_COMPONENT) found = true;
    n.content?.forEach(visit);
  };
  if (doc) visit(doc);
  return found;
}

/**
 * The v2 definition of a builder document made by `interfaceDocument`. Blocks moved or inserted in
 * the builder land where they now are (top of the page, a column of the grid, a tab); prose and
 * other nodes the builder may hold are left out.
 */
export function interfaceFromDocument(doc: DocumentNode): RecordInterfaceDefinition {
  const def = emptyInterface();
  const rowsById = new Map<string, GridRow>();
  const rows: GridRow[] = [];
  const cellOf = (rowId: string, rowIndex: number, cellId: string, span: number, tabs?: boolean) => {
    let row = rowsById.get(rowId);
    if (!row) {
      row = { id: rowId, cells: [] };
      rowsById.set(rowId, row);
      rows[rowIndex] = row;
    }
    let cell = row.cells.find((c) => c.id === cellId);
    if (!cell) {
      cell = { id: cellId, span, blocks: [], ...(tabs ? { tabs: true } : {}) };
      row.cells.push(cell);
    }
    return cell;
  };
  const tabOf = (id: string, label: LocalizedText) => {
    let tab = def.regions.tabs.find((t) => t.id === id);
    if (!tab) {
      tab = { id, label, blocks: [] };
      def.regions.tabs.push(tab);
    }
    return tab;
  };
  let seq = 0;
  const freshId = (base: string) => {
    let id: string;
    do id = `${base}_${++seq}`;
    while (def.blocks[id] || rowsById.has(id) || def.regions.tabs.some((t) => t.id === id));
    return id;
  };
  /** Where a node sits: the column of a columns node, or a tab. */
  type Where = { row?: number; col?: number; cols?: number; tab?: { id: string; label: LocalizedText } };
  const place = (rawId: string, block: InterfaceBlock, p: BlockPlacement | undefined, where: Where) => {
    const id = def.blocks[rawId] ? freshId(rawId) : rawId;
    def.blocks[id] = block;
    if (where.tab) {
      tabOf(where.tab.id, where.tab.label).blocks.push(id);
      return;
    }
    if (where.col !== undefined && where.row !== undefined) {
      const cols = where.cols ?? 1;
      const isRail = where.row === 0 && cols >= 2 && where.col === cols - 1;
      if (isRail) {
        def.regions.rail.push(id);
        return;
      }
      const rowId = p?.row?.id ?? `row_${where.row + 1}`;
      const cellId = p?.cell?.id ?? `cell_${where.row + 1}_${where.col + 1}`;
      const span = p?.cell?.span ?? (where.col === 0 && cols > 2 ? 4 : 8);
      cellOf(rowId, where.row, cellId, span, p?.cell?.tabs).blocks.push(id);
      return;
    }
    if (p?.region === 'keyFacts') def.regions.keyFacts.push(id);
    else if (p?.region === 'createForm') def.regions.createForm.push(id);
    else if (p?.region === 'rail') def.regions.rail.push(id);
    else if (block.type === 'stage-bar' && !def.regions.stageBar) def.regions.stageBar = id;
    else def.regions.header.push(id);
  };
  let columnsSeen = 0;
  const visit = (n: DocumentNode, where: Where) => {
    if (n.type === 'uiBlock' && n.attrs?.component === RECORD_BLOCK_COMPONENT) {
      const props = n.attrs.props as { block?: unknown; place?: BlockPlacement } | undefined;
      const parsed = InterfaceBlock.safeParse(props?.block);
      const id =
        typeof n.attrs.id === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(n.attrs.id)
          ? n.attrs.id
          : freshId('block');
      if (parsed.success) place(id, parsed.data, props?.place, where);
      return;
    }
    if (n.type === 'columns') {
      const row = columnsSeen++;
      const cols = n.content?.length ?? 0;
      n.content?.forEach((c, col) => c.content?.forEach((child) => visit(child, { row, col, cols })));
      return;
    }
    if (n.type === 'tab') {
      // The editor keeps only a tab's title: its id and localized label travel on its blocks.
      const title = typeof n.attrs?.title === 'string' ? n.attrs.title.trim().slice(0, 40) : '';
      const places = (n.content ?? []).map(
        (c) => (c.attrs?.props as { place?: BlockPlacement } | undefined)?.place,
      );
      const known = places
        .map((p) => p?.tab)
        .find((t): t is NonNullable<BlockPlacement['tab']> => Boolean(t));
      const host = places
        .map((p) => p?.host)
        .find((h): h is NonNullable<BlockPlacement['host']> => Boolean(h));
      const id = known && !def.regions.tabs.some((t) => t.id === known.id) ? known.id : freshId('tab');
      const label: LocalizedText =
        known && (!title || resolveText(known.label, 'en') === title) ? known.label : title || id;
      tabOf(id, label);
      n.content?.forEach((child) => visit(child, { tab: { id, label } }));
      if (where.row !== undefined && where.col !== undefined) {
        // The tabs live in the cell of this column: the one its own blocks created, else the cell
        // the tab blocks name as their host (a cell with nothing but the tabs).
        const existing = host ? undefined : rows[where.row]?.cells[where.col];
        const cell = cellOf(
          rows[where.row]?.id ?? host?.row.id ?? `row_${where.row + 1}`,
          where.row,
          host?.cell.id ?? existing?.id ?? `cell_${where.row + 1}_${where.col + 1}`,
          host?.cell.span ?? existing?.span ?? 8,
        );
        cell.tabs = true;
      }
      return;
    }
    n.content?.forEach((child) => visit(child, where));
  };
  doc.content?.forEach((n) => visit(n, {}));
  def.regions.main.rows = rows.filter((r): r is GridRow => Boolean(r));
  // One cell hosts the tabs.
  let tabsSeen = false;
  for (const row of def.regions.main.rows)
    for (const cell of row.cells) {
      if (cell.tabs && tabsSeen) delete cell.tabs;
      if (cell.tabs) tabsSeen = true;
    }
  return def;
}
